import { TPaymentAccordionItemInstance } from 'widgets/checkout/PaymentAccordionItem';
import { TCorePaymentAccordionItemInstance } from './CorePaymentAccordionItem';
import { getJSONByUrl } from 'widgets/toolbox/ajax';
import { getUrlParams, log } from 'widgets/toolbox/util';

type TOrder = {
    billing: {
        payment: {
            applicablePaymentMethods: [
                {
                    [key: string]: string;
                }
            ];
            restrictedPaymentMethods: Array<string> | [];
        };
    };
    totals: {
        isZeroTotal: boolean;
        isGCZeroTotal: boolean;
    };
    priceTotal: {
        value: number;
    };
};

interface IPaymentMethodsResponse {
    [renderedOptions: string]: Record<string, string>;
}

const isPaymentMethodsResponse = (value: Record<string, unknown>): value is IPaymentMethodsResponse => {
    return Object.hasOwnProperty.call(value, 'renderedOptions');
};

/**
 * @description Payment accordion
 * @param PaymentAccordion Base widget for extending
 * @returns CorePaymentAccordion class
 */
function CorePaymentAccordionClassCreator(PaymentAccordion: import('widgets/checkout/PaymentAccordion').TPaymentAccordion) {
    class CorePaymentAccordion extends PaymentAccordion {
        ZERO_PAYMENT_CLASS = 'm-zero_payment';

        ZERO_PAYMENT_ID = 'ZERO_PAYMENT';

        AFTERPAY_ID = 'AFTERPAY';

        GIFT_CERTIFICATE_ID = 'GIFT_CERTIFICATE';

        lastResponseHandler: { drop: boolean; onResponse: (response: IPaymentMethodsResponse) => void } | null = null;

        isAddPaymentMethodsInFlight: boolean = false;

        orderPriceTotal: number = -1;

        restrictedPaymentMethods: Array<string> | [] = [];

        erroredPaymentMethod: string | undefined;

        erroredAdyenMethod: string | undefined;

        prefs() {
            return {
                checkoutStepSectionClass: '.b-checkout_step-section',
                gcZeroTotal: false,
                wasGCZeroTotal: false,
                ...super.prefs()
            };
        }

        /**
         * @description Get payment methods content and render them.
         * @param methodsToAdd - Methods ids that should be added
         * @param applicablePaymentMethodIds - Applicable payment methods ids that helps to find append position
         */
        addPaymentMethods(methodsToAdd: Array<string> = [], applicablePaymentMethodIds: Array<string>) {
            if (this.lastResponseHandler) {
                this.lastResponseHandler.drop = true;
            }

            const responseHandler = {
                drop: false,
                onResponse: response => {
                    if (isPaymentMethodsResponse(response)) {
                        Object.entries(response.renderedOptions).forEach(([method, methodContent]) => {
                            const index = applicablePaymentMethodIds.indexOf(method);

                            this.addPMAt(methodContent, index);
                        });
                    }
                }
            };

            this.lastResponseHandler = responseHandler;
            this.isAddPaymentMethodsInFlight = true;

            return getJSONByUrl(this.prefs().paymentOptionsUrl, {
                methods: methodsToAdd.join(',')
            }).then(response => {
                if (responseHandler.drop) {
                    // a new request was sent, so we don't need to process this response
                    return;
                }

                responseHandler.onResponse(response);
                this.isAddPaymentMethodsInFlight = false;
            });
        }

        /**
         * @description Add payment method content at a specific index
         * Uses index ref elements added in cartridges/app_custom_core/cartridge/templates/default/checkout/billing/paymentOptions.isml
         * as some PMs add multiple nodes
         * @param content - HTML content of the payment method
         * @param index - Index at which to insert the payment method
         */
        addPMAt(content: string, index: number) {
            const indexNode = this.ref(`pmIndex${index}`).get();
            const container = this.ref('container')?.els?.[0];

            if (!container) {
                return;
            }

            if (!indexNode) {
                this.ref('container').append(content.trim(), index);

                if (!PRODUCTION) {
                    log.warn('Payment Method index node not found, falling back to append');
                }

                return; // Exit after fallback to prevent duplicate insertion
            }

            const tempEl = document.createElement('div');

            tempEl.innerHTML = content.trim();
            Array.from(tempEl.childNodes).forEach(child => {
                container.insertBefore(child, <Node>indexNode);
            });
        }

        /**
         * @description Initialize widget logic
         * @listens "giftcertificate.updated"
         */
        init() {
            super.init();

            this.eventBus().on('giftcertificate.updated', 'updatePaymentOptions');

            const urlParams = getUrlParams();

            this.erroredPaymentMethod = urlParams.paymentMethod;
            this.erroredAdyenMethod = urlParams.adyenMethod;
        }

        handleChildInit(child: TPaymentAccordionItemInstance) {
            super.handleChildInit(child);

            const previouslyOpened = <TPaymentAccordionItemInstance | null>this.currentItem;
            const isAlreadyOpened =
                previouslyOpened?.config?.methodId === child.prefs().methodId && previouslyOpened?.config?.methodName === child.prefs().methodName;
            const isTheOneThatErroredOut =
                this.erroredPaymentMethod === child.config?.methodId &&
                (!this.erroredAdyenMethod || this.erroredAdyenMethod === child.config?.adyenType);

            if (isAlreadyOpened || isTheOneThatErroredOut) {
                (child as TCorePaymentAccordionItemInstance).openPanel(false);
            }
        }

        /**
         * @description Handles current payment tab submit
         * @param attributes need attributes for payment items
         */
        process(attributes: Record<string, unknown>): Promise<Record<string, unknown>> {
            if (this.prefs().gcZeroTotal) {
                const fieldName = this.prefs().fieldName;

                return Promise.resolve({ [fieldName]: this.GIFT_CERTIFICATE_ID });
            }

            if (this.ref('self').hasClass(this.ZERO_PAYMENT_CLASS)) {
                const fieldName = this.prefs().fieldName;

                return Promise.resolve({ [fieldName]: this.ZERO_PAYMENT_ID });
            }

            if (this.prefs().disabled) {
                return Promise.resolve({});
            }

            return new Promise((resolve, reject) => {
                const CHECKOUT_BILLING_ERROR = 'checkout.billing.step.error';
                const valid = this.validate();

                const currentPaymentItem = this.getCurrentPaymentItem();

                if (!currentPaymentItem) {
                    reject(new Error(this.prefs().errorMessage));

                    this.eventBus().emit(CHECKOUT_BILLING_ERROR, this);

                    return;
                }

                if (!valid) {
                    reject();

                    this.eventBus().emit(CHECKOUT_BILLING_ERROR, this);

                    return;
                }

                if (currentPaymentItem) {
                    const tabProcessResult = currentPaymentItem.process(attributes).then(data => {
                        // @ts-expect-error data should be object in order to apply spread operator
                        return { [this.prefs().fieldName]: currentPaymentItem.prefs().methodId, ...data };
                    });

                    resolve(tabProcessResult);

                    return;
                }

                this.eventBus().emit(CHECKOUT_BILLING_ERROR, this);

                reject();
            });
        }

        private hasNoChanges(isOrderPriceTotalChanged: boolean, order: TOrder, gcStateChanged: boolean): boolean {
            return (
                !gcStateChanged &&
                (this.prefs().disabled || (!this.isUpdatingRequired(order) && !isOrderPriceTotalChanged && !this.hasUnrenderedApplicable(order)))
            );
        }

        // Retry render when an applicable method produced empty content on a previous AJAX render
        // (e.g. Checkout.com session couldn't be created before billing address was committed).
        private hasUnrenderedApplicable(order: TOrder): boolean {
            if (this.isAddPaymentMethodsInFlight) {
                return false;
            }

            const container = this.ref('container')?.els?.[0] as HTMLElement | undefined;

            if (!container) {
                return false;
            }

            return this.getApplicablePayments(order).some(method => {
                return !container.querySelector(`[data-method-id="${method.ID}"]`);
            });
        }

        private updateGCState(isNowGCZeroTotal: boolean): void {
            this.setPref('disabled', isNowGCZeroTotal);
            this.setPref('gcZeroTotal', isNowGCZeroTotal);
        }

        private hasInitialized = true;

        /**
         * @description Adjust payment methods list after order updating
         * @param order - Order model
         */
        updatePaymentOptions(order: TOrder) {
            const isOrderPriceTotalChanged = this.isOrderPriceTotalChanged(order);
            const isNowGCZeroTotal = order.totals.isGCZeroTotal;

            const gcStateChanged = this.prefs().wasGCZeroTotal !== isNowGCZeroTotal;

            this.setPref('wasGCZeroTotal', isNowGCZeroTotal);

            this.restrictedPaymentMethods = order.billing.payment.restrictedPaymentMethods || [];

            if (this.hasInitialized) {
                this.hasInitialized = false;
                this.orderPriceTotal = order.priceTotal?.value;
            }

            if (this.hasNoChanges(isOrderPriceTotalChanged, order, gcStateChanged)) {
                return;
            }

            this.updateGCState(isNowGCZeroTotal);

            const renderedPaymentMethods = this.getRenderedMethods();
            const applicablePaymentMethods = this.getApplicablePayments(order);
            const applicablePaymentMethodIds: Array<string> = applicablePaymentMethods.map(method => method.ID);

            const methodsToRender: Array<string> = this.getMethodsToRender(
                applicablePaymentMethodIds,
                renderedPaymentMethods,
                isOrderPriceTotalChanged
            );
            const methodsToDelete: Array<string> = this.getMethodsToDelete(
                applicablePaymentMethodIds,
                renderedPaymentMethods,
                isOrderPriceTotalChanged
            );

            const isAddZeroTotal = this.isZeroPaymentAdded(order, methodsToRender);
            const isRemoveZeroTotal = this.isZeroPaymentRemoved(order, methodsToDelete);
            const selfElement = this.ref('self').get() as HTMLElement;

            if (isAddZeroTotal) {
                this.ref('container').empty();

                if (!isNowGCZeroTotal) {
                    this.addPaymentMethods(methodsToRender, applicablePaymentMethodIds);
                }

                this.ref('self').addClass(this.ZERO_PAYMENT_CLASS);
                selfElement.closest(this.prefs().checkoutStepSectionClass)?.classList.add(this.ZERO_PAYMENT_CLASS);
                this.ref('self').data('singlePanel', 'true');

                setTimeout(() => {
                    this.eachChild<TPaymentAccordionItemInstance>(item => {
                        item.openPanel();
                    });
                }, 500);
            } else {
                if (methodsToDelete.length) {
                    this.removePaymentOption(methodsToDelete);
                }

                if (methodsToRender.length) {
                    this.addPaymentMethods(methodsToRender, applicablePaymentMethodIds).then(() => {
                        if (isRemoveZeroTotal) {
                            this.ref('self').removeClass(this.ZERO_PAYMENT_CLASS);
                            selfElement.closest(this.prefs().checkoutStepSectionClass)?.classList.remove(this.ZERO_PAYMENT_CLASS);
                            this.ref('self').data('singlePanel', 'false');
                        }

                        setTimeout(() => {
                            this.activate();
                        }, 800);
                    });
                }
            }

            this.applicablePaymentMethods = applicablePaymentMethods;
            this.orderPriceTotal = order.priceTotal?.value;
        }

        getMethodsToRender(applicablePaymentMethodIds: Array<string>, renderedPaymentMethods: Array<string>, isOrderPriceTotalChanged: boolean) {
            const methodsToRender: Array<string> = [];

            applicablePaymentMethodIds.forEach(applicablePaymentMethodID => {
                if (!renderedPaymentMethods.includes(applicablePaymentMethodID)) {
                    methodsToRender.push(applicablePaymentMethodID);
                }
            });

            if (isOrderPriceTotalChanged && applicablePaymentMethodIds.includes(this.AFTERPAY_ID) && !methodsToRender.includes(this.AFTERPAY_ID)) {
                methodsToRender.push(this.AFTERPAY_ID);
            }

            return methodsToRender;
        }

        getMethodsToDelete(applicablePaymentMethodIds: Array<string>, renderedPaymentMethods: Array<string>, isOrderPriceTotalChanged: boolean) {
            const methodsToDelete: Array<string> = [];

            renderedPaymentMethods.forEach(renderedPaymentMethod => {
                if (!applicablePaymentMethodIds.includes(renderedPaymentMethod)) {
                    methodsToDelete.push(renderedPaymentMethod);
                }
            });

            if (isOrderPriceTotalChanged && applicablePaymentMethodIds.includes(this.AFTERPAY_ID) && !methodsToDelete.includes(this.AFTERPAY_ID)) {
                methodsToDelete.push(this.AFTERPAY_ID);
            }

            return methodsToDelete;
        }

        isOrderPriceTotalChanged(order) {
            if (this.orderPriceTotal > -1 && this.orderPriceTotal !== order.priceTotal?.value) {
                return true;
            }

            return false;
        }

        isZeroPaymentAdded(order, methodsToRender) {
            return !!(
                (order.totals.isZeroTotal && methodsToRender.length === 1 && methodsToRender[0] === 'ZERO_PAYMENT') ||
                order.totals.isGCZeroTotal
            );
        }

        isZeroPaymentRemoved(order, methodsToDelete) {
            return !!(
                (!order.totals.isZeroTotal && methodsToDelete.length === 1 && methodsToDelete[0] === 'ZERO_PAYMENT') ||
                !order.totals.isGCZeroTotal
            );
        }
    }

    return CorePaymentAccordion;
}

export type TCorePaymentAccordion = ReturnType<typeof CorePaymentAccordionClassCreator>;

export type TCorePaymentAccordionInstance = InstanceType<TCorePaymentAccordion>;

export default CorePaymentAccordionClassCreator;
